import type {ReactElement} from 'react';
import {useState} from 'react';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {CustomFeedbackIcon} from '@Components/ai-chat-window/components/custom-feedback-icon/custom-feedback-icon';
import {ThumbsUpFilledIcon} from '@Components/ai-chat-window/components/thumbs-up-filled-icon/thumbs-up-filled-icon';
import {ThumbsDownFilledIcon} from '@Components/ai-chat-window/components/thumbs-down-filled-icon/thumbs-down-filled-icon';
import {Icon} from '@Components/icon-v2/icon';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {isTouchDevice} from '@Utils/browser.util';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {useAiChatActions} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {recordFeedback} from '@Libraries/ai-chat.library';
import type {EntityCardActionContext, RegisteredEntityCardActionSpec} from './actions/entity-card-action.types';
import type {EntityCardProps} from './entity-card.types';
import {STATUS_TONE_PILL_TYPE, THUMBS_DOWN_ICON, THUMBS_UP_ICON} from './entity-card.types';
import {getEntityCardAction} from './actions/entity-card-action-registry';
import {Pill} from '@Components/pill/pill';
import {PillSize} from '@Components/pill/pill.types';
import {EntityCardThumbnail} from './components/entity-card-thumbnail/entity-card-thumbnail';
import {useRefreshedEntityCard} from './entity-card.hooks';
import {shownMetaLineOf} from './entity-card.library';
import {UseThisButton} from '@Components/ai-chat-window/components/use-this-button/use-this-button';
import {UsedAssetKind} from '@AiChat/callbacks/ai-chat-callbacks.types';
import styles from './entity-card.module.scss';

export function EntityCard(streamedCard: EntityCardProps): ReactElement {
  const {title, meta, alert, mediaUrl, icon, mediaCount, status, entityType, entity} = useRefreshedEntityCard(streamedCard);
  const actions = useAiChatActions();
  const {chatId, messageId} = useAiChatMessage();
  const dispatch = useAppDispatch();
  const message = useAppSelector((state) => state.aiChatWindow[chatId]?.messages?.[messageId]);
  const [runningActionIcon, setRunningActionIcon] = useState<string | null>(null);
  const action = getEntityCardAction(entityType);

  let feedback = null;
  if (message?.role === MessageRole.ASSISTANT) {
    feedback = message.feedback;
  }

  const actionContext: EntityCardActionContext = {
    openPdf: actions.openPdf,
    chatId,
    feedback,
    recordFeedback: (feedbackType): void => {
      void recordFeedback(chatId, messageId, feedbackType, null, null);

      if (message?.role !== MessageRole.ASSISTANT) {
        return;
      }

      dispatch(
        aiChatWindowActions.updateMessage({
          chatId,
          messageId,
          message: {...message, feedback: {type: feedbackType, comment: null, negativeFeedbackPreset: null}},
        })
      );
    },
  };

  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }
    return <Text val={title} size={TextSize.LG} bold className={styles.entityCardTitle} />;
  };

  const renderMeta = (): ReactElement | null => {
    if (meta.length === 0) {
      return null;
    }

    return <Text val={shownMetaLineOf(meta, alert)} size={TextSize.MD} className={styles.entityCardMeta} />;
  };

  const renderStatus = (): ReactElement | null => {
    if (status === undefined || status.label === '') {
      return null;
    }

    return <Pill label={status.label} type={STATUS_TONE_PILL_TYPE[status.tone]} size={PillSize.SM} />;
  };

  const runCardAction = async (spec: RegisteredEntityCardActionSpec): Promise<void> => {
    setRunningActionIcon(spec.icon);

    try {
      await spec.run(entity ?? {}, actionContext);
    } finally {
      setRunningActionIcon(null);
    }
  };

  const renderSelectedThumb = (spec: RegisteredEntityCardActionSpec): ReactElement | null => {
    if (spec.icon === THUMBS_UP_ICON) {
      return <CustomFeedbackIcon cursorClassname="_cursor-pointer" tooltipText={window.i18next.t(spec.ariaLabelKey)} svg={<ThumbsUpFilledIcon />} />;
    }

    if (spec.icon === THUMBS_DOWN_ICON) {
      return <CustomFeedbackIcon cursorClassname="_cursor-pointer" tooltipText={window.i18next.t(spec.ariaLabelKey)} svg={<ThumbsDownFilledIcon />} />;
    }

    return null;
  };

  const renderCardAction = (spec: RegisteredEntityCardActionSpec): ReactElement => {
    const selectedThumb = renderSelectedThumb(spec);

    if (spec.isSelected?.(actionContext) === true && selectedThumb !== null) {
      return <div key={spec.icon}>{selectedThumb}</div>;
    }

    const label = window.i18next.t(spec.ariaLabelKey);

    return (
      <OverlayTrigger
        key={spec.icon}
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{label}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
        offset={undefined}
      >
        <Icon
          icon={spec.icon}
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          ariaLabel={label}
          isLoading={runningActionIcon === spec.icon}
          onClick={(): void => {
            void runCardAction(spec);
          }}
        />
      </OverlayTrigger>
    );
  };

  const renderActions = (): ReactElement | null => {
    if (!action?.cardActions.length) {
      return null;
    }

    return (
      <div className={styles.entityCardActions}>
        {action.cardActions.map((spec) => {
          return renderCardAction(spec);
        })}
      </div>
    );
  };

  const renderUseThis = (): ReactElement | null => {
    if (entityType === undefined || entity === undefined) {
      return null;
    }

    return <UseThisButton asset={{kind: UsedAssetKind.ENTITY, chatId, entityType, entity}} />;
  };

  const renderContent = (): ReactElement => {
    return (
      <>
        <EntityCardThumbnail altText={title ?? ''} mediaUrl={mediaUrl} icon={icon} mediaCount={mediaCount} />
        <div className={styles.entityCardText}>
          {renderTitle()}
          {renderMeta()}
          {renderStatus()}
        </div>
      </>
    );
  };

  if (action === undefined || entity === undefined) {
    return (
      <div className={styles.entityCardWrapper}>
        <div className={styles.entityCard}>{renderContent()}</div>
        {renderActions()}
        {renderUseThis()}
      </div>
    );
  }

  const handleClick = (): void => {
    const entityId = action.pointedAtIdOf?.(entity) ?? '';
    if (entityId !== '' && messageId !== '') {
      dispatch(aiChatWindowActions.setPointedAtEntity({chatId, pointedAtEntity: {messageId, entityType: action.entityType, entityId}}));
    }
    void action.run(entity, actionContext);
  };

  return (
    <div className={styles.entityCardWrapper}>
      <ClickableDiv className={`${styles.entityCard} ${styles.clickableEntityCard}`} onClick={handleClick}>
        {renderContent()}
      </ClickableDiv>
      {renderActions()}
      {renderUseThis()}
    </div>
  );
}
